import { DashboardLayout } from "@/projects/components/dashboard-layout";
import {
  createPathshala,
  deletePathshala,
  fetchPathshalasPage,
  getPathshalaApiErrorMessage,
  setPathshalaActive,
  updatePathshala,
  type Pathshala,
  type PathshalaWritePayload,
} from "@/projects/lib/pathshalasApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/components/ui/table";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Switch } from "@/projects/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/projects/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/projects/components/ui/alert-dialog";
import { useEffect, useState } from "react";
import { Plus, Edit, Power, Trash2, Loader2, Search } from "lucide-react";
import { toast } from "@/projects/lib/toast";
import { cn } from "@/projects/lib/utils";
import {
  DataPagination,
  useServerPagination,
} from "@/projects/components/data-pagination";

const EMPTY_FORM = {
  name: "",
  isActive: true,
};

export function PathshalasPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Pathshala | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<Pathshala | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Pathshala | null>(null);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => window.clearTimeout(t);
  }, [q]);

  const pagination = useServerPagination(
    ({ page, pageSize }) =>
      fetchPathshalasPage({
        page,
        pageSize,
        search: debouncedQ || undefined,
      }),
    [debouncedQ],
    {
      onError: error =>
        toast.error(
          getPathshalaApiErrorMessage(error, "Failed to load pathshalas.")
        ),
    }
  );
  const pathshalas = pagination.items;
  const loading = pagination.loading;

  return (
    <DashboardLayout
      role="admin"
      title="Pathshalas"
      breadcrumb={["Home", "Pathshalas"]}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search pathshalas…"
              className="w-64 pl-8"
            />
          </div>
          <div className="text-muted-foreground text-sm">
            {loading ? "Loading…" : `${pagination.meta.count} pathshalas`}
          </div>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Pathshala
        </Button>
      </div>

      <div className="bg-card shadow-soft overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[1%] whitespace-nowrap">No.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} className="py-16 text-center">
                    <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading pathshalas…
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && pathshalas.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-muted-foreground py-16 text-center text-sm"
                  >
                    No pathshalas found. Add your first pathshala to get
                    started.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                pathshalas.map((p, i) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-muted-foreground whitespace-nowrap tabular-nums">
                      {pagination.startIndex + i + 1}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                          p.isActive
                            ? "border-success/30 bg-success/10 text-success"
                            : "border-muted-foreground/30 bg-muted text-muted-foreground"
                        )}
                      >
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            p.isActive ? "bg-success" : "bg-muted-foreground"
                          )}
                        />
                        {p.isActive ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditing(p);
                            setOpen(true);
                          }}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmToggle(p)}
                        >
                          <Power className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(p)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        {!loading && pathshalas.length > 0 && (
          <DataPagination pagination={pagination} itemLabel="pathshalas" />
        )}
      </div>

      <PathshalaFormDialog
        open={open}
        onOpenChange={setOpen}
        pathshala={editing}
        onSaved={() => pagination.reload()}
      />

      <AlertDialog
        open={!!confirmToggle}
        onOpenChange={o => !o && setConfirmToggle(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmToggle?.isActive ? "Deactivate" : "Activate"} pathshala?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmToggle?.isActive
                ? `"${confirmToggle?.name}" will be marked inactive.`
                : `"${confirmToggle?.name}" will be marked active.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={toggling}
              onClick={async e => {
                e.preventDefault();
                if (!confirmToggle) {
                  return;
                }
                setToggling(true);
                try {
                  await setPathshalaActive(
                    confirmToggle.id,
                    !confirmToggle.isActive
                  );
                  toast.success(
                    `Pathshala ${confirmToggle.isActive ? "deactivated" : "activated"}`
                  );
                  setConfirmToggle(null);
                  pagination.reload();
                } catch (error) {
                  toast.error(
                    getPathshalaApiErrorMessage(
                      error,
                      "Failed to update pathshala status."
                    )
                  );
                } finally {
                  setToggling(false);
                }
              }}
            >
              {toggling ? "Saving…" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={o => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete pathshala?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{confirmDelete?.name}". This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async e => {
                e.preventDefault();
                if (!confirmDelete) {
                  return;
                }
                setDeleting(true);
                try {
                  await deletePathshala(confirmDelete.id);
                  toast.success("Pathshala deleted");
                  setConfirmDelete(null);
                  pagination.reload();
                } catch (error) {
                  toast.error(
                    getPathshalaApiErrorMessage(
                      error,
                      "Failed to delete pathshala."
                    )
                  );
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function PathshalaFormDialog({
  open,
  onOpenChange,
  pathshala,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pathshala: Pathshala | null;
  onSaved: () => void;
}) {
  const [f, setF] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setF(
      pathshala
        ? {
            name: pathshala.name,
            isActive: pathshala.isActive,
          }
        : EMPTY_FORM
    );
  }, [open, pathshala]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = f.name.trim();
    if (!name) {
      toast.error("Pathshala name is required.");
      return;
    }

    setSaving(true);
    try {
      const payload: PathshalaWritePayload = {
        name,
        is_active: f.isActive,
      };
      if (pathshala) {
        await updatePathshala(pathshala.id, payload);
        toast.success("Pathshala updated");
      } else {
        await createPathshala(payload);
        toast.success("Pathshala added successfully");
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(
        getPathshalaApiErrorMessage(
          error,
          pathshala ? "Failed to update pathshala." : "Failed to add pathshala."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {pathshala ? "Edit Pathshala" : "Add Pathshala"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={e => void handleSubmit(e)} className="grid gap-3">
          <div>
            <Label className="text-xs">Pathshala Name *</Label>
            <Input
              value={f.name}
              onChange={e => setF({ ...f, name: e.target.value })}
              placeholder="e.g. New Pathshala"
              disabled={saving}
            />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Switch
              checked={f.isActive}
              onCheckedChange={v => setF({ ...f, isActive: v })}
              disabled={saving}
            />
            <Label className="text-sm">
              {f.isActive ? "Active" : "Inactive"}
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {pathshala ? "Save Changes" : "Add Pathshala"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
